export enum ContentEnum {
  COURSE = "Course",
  CLASS = "Class",
  SUBJECT = "Subject",
  MODULE = "Module",
  CHAPTER = "Chapter",
  SUBJECT_CHAPTER = "SubjectChapter",
  TOPIC = "Topic",
  CONCEPT = "Concept",
  QUESTION = "Question",
  VIDEO = "Video",
}

export const MappingLevelsEnum = {
  SUBJECT: ContentEnum.SUBJECT,
  CHAPTER: ContentEnum.CHAPTER,
  TOPIC: ContentEnum.TOPIC,
  CONCEPT: ContentEnum.CONCEPT,
};

// Design Library level picker — the ContentEnum subset that has generic design docs.
export enum ContentDesignLibraryOptionsEnum {
  SUBJECT = ContentEnum.SUBJECT,
  CHAPTER = ContentEnum.CHAPTER,
  TOPIC = ContentEnum.TOPIC,
  CONCEPT = ContentEnum.CONCEPT,
}

// Per-asset-type library fork family (POST content-library/{asset}s/{libraryId}/fork).
export enum LibraryAssetTypeEnum {
  LECTURE = "lecture",
  VIDEO = "video",
  TEACHING_AID = "teaching-aid",
}
export enum ContentTypeIdsEnum {
  COURSE = 1,
  CLASS = 2,
  SUBJECT = 3,
  MODULE = 4,
  CHAPTER = 5,
  SUBJECTCHAPTER = 6,
  TOPIC = 7,
  CONCEPT = 8,
  QUESTION = 9,
  VIDEO = 10,
  // ── Course-scoped levels (QR code library) ────────────────────────────────
  // These are copies of a content node INSIDE one course, NOT the generic tree nodes above.
  // CHAPTERTOPIC (12) is not TOPIC (7), and TOPICCONCEPT (13) is not CONCEPT (8): the generic
  // nodes identify content in the abstract, the course-scoped ones identify one course's own
  // copy of it. Conflating them makes a QR code resolve to the wrong course's content — which
  // is precisely the defect the QR code library redesign exists to fix.
  CHAPTERTOPIC = 12,
  TOPICCONCEPT = 13,
  ACTIVITY = 15,
}

export enum ContentTypesEnum {
  COURSE = ContentEnum.COURSE,
  CLASS = ContentEnum.CLASS,
  SUBJECT = ContentEnum.SUBJECT,
  MODULE = ContentEnum.MODULE,
  CHAPTER = ContentEnum.CHAPTER,
}

export enum SearchContentEnum {
  COURSE = ContentEnum.COURSE,
  CLASS = ContentEnum.CLASS,
  SUBJECT = ContentEnum.SUBJECT,
  MODULE = ContentEnum.MODULE,
  CHAPTER = ContentEnum.CHAPTER,
  TOPIC = ContentEnum.TOPIC,
  CONCEPT = ContentEnum.CONCEPT,
  QUESTION = ContentEnum.QUESTION,
}

export enum QuestionLevelIdEnum {
  LEVEL1 = 1,
  LEVEL2 = 2,
}

export enum QuestionTypeIdsEnum {
  OBJECTIVE = 1,
  SUBJECTIVE = 2,
  MULTIPLE_CHOICE = 3,
  NUMERICAL = 4,
  COMPREHENSION = 6,
  MATCH_THE_COLUMN = 7,
  TRUE_FALSE = 8,
  CASE_BASED = 12,
}

export enum QuestionSubtypeIdsEnum {
  ASSERTION_REASON = 11,
  SUBJECTIVE_VSA = 21,
  SUBJECTIVE_SA = 22,
  SUBJECTIVE_LA = 23,
  INTEGER_TYPE = 41,
}

export enum DifficultyLevelEnum {
  EASY = 1,
  MEDIUM = 2,
  HARD = 3,
}

export enum DifficultyGroupEnum {
  BASIC = "Basic",
  EASY = "Easy",
  NORMAL = "Normal",
  HARD = "Hard",
  TOUGH = "Tough",
}

export enum QuestionLevelIdsEnum {
  LEVEL1 = 1,
  LEVEL2 = 2,
}

export enum QuestionCategoryIdsEnum {
  GENERAL = 1,
  EXAMPLE = 2,
  ASSIGNMENT = 3,
  QUIZ = 4,
  TEST = 5,
  RECOMMENDED = 9,
  INSTITUTE = 10,
  SMARTBOOK = 11,
  WORKBOOK = 12,
  CASE_BASED = 13,
}

export enum ActiveQuestionCategoryIdsEnum {
  ALL = QuestionCategoryIdsEnum.GENERAL,
  RECOMMENDED = QuestionCategoryIdsEnum.RECOMMENDED,
  TEST = QuestionCategoryIdsEnum.TEST,
  ASSIGNMENT = QuestionCategoryIdsEnum.ASSIGNMENT,
  INSTITUTE = QuestionCategoryIdsEnum.INSTITUTE,
}

export enum QuestionSelectorSortEnum {
  DIFFICULTY = 1,
  CONCEPTS = 2,
  NEWEST = 3,
}
export enum QuestionBloomsTaxonomyIdsEnum {
  REMEMBERING = 1,
  UNDERSTANDING = 2,
  APPLYING = 3,
  ANALYZING = 4,
  EVALUATING = 5,
  CREATING = 6,
}

export enum QuestionIsActiveEnum {
  ACTIVE = 1,
  SUSPENDED = 2,
}

export enum QuestionDisplayTypeEnum {
  HTML = "RenderedHtml",
  DESKTOP = "DesktopImage",
  MOBILE = "MobileImage",
  RAW_IMAGE = "RawQuestionUrl",
}

export enum SolutionDisplayTypeEnum {
  HTML = "SolutionHtml",
  DESKTOP = "SolutionDesktopImage",
  MOBILE = "SolutionMobileImage",
  RAW_IMAGE = "RawSolutionUrl",
}

export enum QuestionPdfImageTypeEnum {
  PAGE = "page",
  QUESTION = "question",
  SOLUTION = "solution",
}

export enum QuestionSerialNumberClassEnum {
  IS_TAGGED = "success",
  NOT_TAGGED = "secondary",
  IS_SELECTED = "primary",
}

export enum QuestionPdfStatusEnum {
  PDF_UPLOADED = 1,
  LATEX_PROCESSING = 11,
  LATEX_EXTRACTED = 12,
  LATEX_FAILED = 13,
  AI_PROCESSING = 14,
  AI_BATCH_SUBMITTED = 15,
  RAW_EXTRACTION_PROCESSING = 21,
  RAW_EXTRACTION_COMPLETE = 22,
  RAW_EXTRACTION_INCOMPLETE = 23,
  PDF_PROCESSED = 2,
  PDF_FAILED = 3,
  PDF_VERIFIED = 4,
  IMAGES_EXTRACTED = 5,
  EXTRACTION_FAILED = 6,
  IMAGES_SAVED = 7,
  QUESTION_BANK = 8,
  PDF_ARCHIVED = 9,
  PDF_PARTIALLY_PROCESSED = 10,
}

export enum QuestionDisplayStatusIdEnum {
  NEW_QUESTION = 1,
  LATEX_CONVERTED = 2,
  LATEX_APPROVED = 3,
  LATEX_REJECTED = 4,
  LATEX_REVIEW_REJECTED = 8,
  HTML_CONVERTED = 9,
  HTML_REVIEWED = 10,
  HTML_CONVERSION_FAILED = 11,
  LATEX_EDIT_NEEDED = 12,
  FINAL_APPROVED = 13,
  LATEX_FAILED = 99,
}

export enum QuestionTaggingStatusEnum {
  LATEX_APPROVED = QuestionDisplayStatusIdEnum.LATEX_APPROVED,
  LATEX_REJECTED = QuestionDisplayStatusIdEnum.LATEX_REJECTED,
  LATEX_EDIT_NEEDED = QuestionDisplayStatusIdEnum.LATEX_EDIT_NEEDED,
}

export enum EditQuestionStatusEnum {
  LATEX_REJECTED = QuestionDisplayStatusIdEnum.LATEX_REJECTED,
  LATEX_EDIT_NEEDED = QuestionDisplayStatusIdEnum.LATEX_EDIT_NEEDED,
}

export enum QuestionFinalReviewStatusEnum {
  REJECTED = QuestionDisplayStatusIdEnum.LATEX_EDIT_NEEDED,
  APPROVED = QuestionDisplayStatusIdEnum.FINAL_APPROVED,
}

export enum QuestionReviewTabsEnum {
  VIEW_MODE = "View Mode",
  EDIT_MODE = "Edit Mode",
}

// The fix-question editor walks one part of the question at a time; the Solution step is
// also what flips the latex editor's solution-diagram target (`ShowSolution`).
export enum QuestionEditorStepEnum {
  QUESTION = "Question",
  HINTS = "Hints",
  SOLUTION = "Solution",
}

export enum QuestionEditorScanTabsEnum {
  QUESTION = "Question",
  SOLUTION = "Solution",
}

export enum PdfContentTypeEnum {
  CHAPTER = "chapter",
  QUESTION = "question",
}

export enum QuestionFilterEnum {
  ALL = 1,
  PENDING = 2,
  ERROR = 3,
}

// What a scanned QR code points at. The backend describes the TARGET; the client owns the routing
// (BR-UI-125) — see qrScanResolutionStore.openResolvedTarget for the seven branches.
export enum QrTargetTypeIdEnum {
  PDF = 1,
  PRESENTATION = 2,
  IMAGE = 3,
  VIDEO = 4,
  // Renamed from PLATFORM_LINK to match the QR code library contract vocabulary (handoff §8.3).
  // The integer is unchanged, so no wire behaviour moves.
  ACTIVITY = 5,
  // CHAPTER_SELF_PRACTICE moved here from 5; it never had an activity behind it and would have
  // resolved to ActivityId: null forever. Self-practice is driven by the chapter.
  PRACTICE = 6,
  // QUESTION_HINTS moved here from 5, for the same reason. Hints are driven by the question.
  HINTS = 7,
}

export enum QrImageTypeEnum {
  SIMPLE = 1,
  QR_WITH_TITLE = 2,
  QR_WITH_DETAILS = 3,
  QR_WITH_DETAILS_VERTICAL = 4,
}

// Mirrors backend enums/content.py QrCategoryEnum after the category collapse: the
// per-difficulty chapter tests (10/11/12/19) became CHAPTER_TEST + an ActivitySubtypeId
// (see QR_CATEGORY_SUBTYPE_OPTIONS in constants/content.ts).
// NOTE: that collapse FREED value 19, which the backend has since reassigned to HOMEWORK.
// The two facts are consistent — 19 is no longer a chapter-test variant.
export enum QrCategoryEnum {
  ANSWER_KEY_WORKBOOK = 1,
  ANSWER_KEY_FEB_EDITION = 9,
  ANSWER_KEY_SAMPLE_EDITION = 99,
  VIDEO_CONCEPT = 4,
  VIDEO_ACTIVITY = 5,
  VIDEO_QUESTION_SOLUTION = 6,
  QUESTION_HINTS = 7,
  CHAPTER_SELF_PRACTICE = 8,
  CHAPTER_TEST = 10,
  CONCEPT_QUIZ = 13,
  CONCEPT_QUIZ_PDF = 14,
  CHAPTER_ASSIGNMENT = 15,
  CONCEPT_ASSIGNMENT = 18,
  // Not course-scoped: maps at level 15 Activity and has no course mappings at all.
  HOMEWORK = 19,
}

export enum QrVersionNoEnum {
  V1 = 1,
  V2 = 2,
  V3 = 3,
  V4 = 4,
  V5 = 5,
  V6 = 6,
}

// How the backend resolved a scanned QR code FOR THE CALLING STUDENT. Mirrors backend
// enums/qr_code.py :: QrResolutionSourceEnum. The same printed code resolves differently per
// student, so this drives every branch of the scan UI — a fallback must be visibly marked and an
// ambiguous match must present a chooser, never a silent pick.
export enum QrResolutionSourceEnum {
  SINGLE_MAPPING = 1, // the code reaches exactly one course — navigate straight through
  STUDENT_COURSE = 2, // resolved to the student's own course — the happy path
  PRIMARY_FALLBACK = 3, // student is in NONE of the mapped courses — mark it visibly
  // Renamed from LIBRARY_DIRECT; the wire value stays 4 so nothing on the wire moves.
  CONTENT_DIRECT = 4, // not course-scoped (question hints, homework) — navigate through
  MULTIPLE_COURSE_MATCH = 5, // student is in 2+ mapped courses — show a chooser
}

// Why a QR code was skipped during a bulk fan-out. Values must match the wire union on
// BulkQrMappingSkip.Reason exactly — the UI keys its plain-English copy off them.
export enum BulkQrMappingSkipReasonEnum {
  NOT_FOUND = "NotFound",
  NO_PRIMARY_MAPPING = "NoPrimaryMapping",
  ALREADY_MAPPED = "AlreadyMapped",
  COURSE_CONFLICT = "CourseConflict",
  TOO_MANY_COURSES = "TooManyCourses",
  NOT_COURSE_SCOPED = "NotCourseScoped",
}

export enum SavedTemplatesSortEnum {
  CREATED_ON = 1,
  TEST_NAME = 2,
  DURATION = 3,
  DIFFICULTY = 4,
}

export enum QuestionSimilarityTypeIdsEnum {
  DUPLICATE = 1,
  SIMILAR = 2,
}
export enum QuestionSimilarityStatusEnum {
  NEW = 1,
  APPROVED = 2,
  REJECTED = 3,
}

export enum PreloadedContentTypeEnum {
  PRELOADED_ASSIGNMENTS = 1,
  PRELOADED_TESTS = 2,
  PAST_YEAR_PAPER = 3,
}

export enum ManagePreloadedContentTypeEnum {
  PRELOADED_ASSIGNMENTS = PreloadedContentTypeEnum.PRELOADED_ASSIGNMENTS,
  PRELOADED_TESTS = PreloadedContentTypeEnum.PRELOADED_TESTS,
}

export enum ChapterPdfStatusEnum {
  PDF_PENDING = 1,
  PDF_UPLOADED = 2,
  PDF_CONTENT_PROCESSED = 3,
  PDF_CONTENT_FAILED = 4,
  PDF_CONTENT_REVIEWED = 5,
  PDF_QUESTIONS_PROCESSED = 6,
  PDF_QUESTIONS_FAILED = 7,
  PDF_QUESTIONS_TAGGED = 8,
  PDF_QUESTIONS_REVIEWED = 9,
}

export enum ChapterPdfReviewStatusEnum {
  NOT_REVIEWED = ChapterPdfStatusEnum.PDF_CONTENT_PROCESSED,
  REVIEWED = ChapterPdfStatusEnum.PDF_CONTENT_REVIEWED,
}

export enum SmartBookTypeEnum {
  COURSE_BOOK = 1,
  WORK_BOOK = 2,
}

export enum SmartBooksChapterTabsEnum {
  SUMMARY = "Summary",
  TOPIC = "Topic",
}

export enum ContentSectionsEnum {
  QUESTION = "Question",
  ANSWER = "Answer",
  SOLUTION = "Solution",
}

export enum LanguageIdsEnum {
  ENGLISH = 1,
  HINDI = 2,
  GUJARATI = 3,
}

export enum LearnVideoTypeEnum {
  CONCEPT = 1,
  EXAMPLE = 2,
  LECTURE = 3,
  FAST_REVISION = 4,
  PAST_YEAR_QUESTION = 5,
}

export enum CourseStatusEnum {
  ARCHIVED = 1,
  FROZEN = 2,
  SIGNUP_OPEN = 3,
  ACTIVE = 4,
}

export enum ContentErrorStatusIdsEnum {
  NEW = 1,
  ASSIGNED = 2,
  FIXED = 3,
  REJECTED = 4,
}

export enum QuestionErrorTypeIdsEnum {
  WRONG_QUESTION = 1,
  WRONG_ANSWER = 2,
  WRONG_SOLUTION = 3,
  UNCLEAR_IMAGE = 4,
  CONCEPT_NOT_HELPFUL = 5,
  OTHER_ERROR = 6,
}

export enum FixChapterQuestionsTabsEnum {
  QUESTION_STATUS = "Question Status",
  ERROR_REPORTS = "Error Reports",
  DIAGRAM_QUESTIONS = "Diagram Questions",
  QUESTION_TYPES = "Question Types",
}

export enum QuestionAssetsEnum {
  QUESTION = "Question",
  HINTS = "Hints",
  CONCEPTS = "Concepts",
  SOLUTION = "Solution",
}

export enum QuestionImageTypeIdsEnum {
  QUESTION = 1,
  SOLUTION = 2,
  QUESTION_DIAGRAM = 3,
  SOLUTION_DIAGRAM = 4,
}

export enum LatexSegmentTypeEnum {
  TEXT = "text",
  DIAGRAM = "diagram",
}

export enum ManageQuestionPdfsTabsEnum {
  ACTION_NEEDED = "Action Needed",
  MANAGE_PDFS = "Manage PDFs",
}

export enum ConceptTypeIdsEnum {
  THEORY = 1,
  EXAMPLE = 2,
  QUESTION = 3,
}

export enum VideoPlatformEnum {
  VIMEO = 1,
  YOUTUBE = 2,
  CLOUDFLARE = 3,
  S3 = 4,
}

export enum ChapterQuestionFilterTypeEnum {
  DISPLAY_STATUS = "display-status",
  QUESTION_TYPE = "question-type",
  ERROR_TYPE = "error-type",
}

export enum ContentNameStatusEnum {
  UPDATE_EXISTING_CONTENT_NAME = 1,
  ADD_NEW_CONTENT_NAME = 2,
  USE_EXISTING_CONTENT_NAME = 3,
}
